import DialogContent from '@mui/material/DialogContent';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type UsePackageConfirmationModal = {
  relatedDealsNames?: string[];
  onConfirmChanges?: () => void;
  onUseAsTemplate?: () => void;
  onCancel?: () => void;
};

const Content = ({ relatedDealsNames = [] }: Pick<UsePackageConfirmationModal, 'relatedDealsNames'>) => {
  const { t } = useTranslation();

  const uniqDealNames = useMemo(() => [...new Set(relatedDealsNames)], [relatedDealsNames]);

  return (
    <DialogContent dividers>
      {!!uniqDealNames.length && (
        <>
          <Typography
            variant="body1"
            data-test="explanation"
            sx={{
              mt: 1,
            }}
          >
            {t('This package is utilized by those deals:')}
          </Typography>
          <List
            sx={{
              listStyleType: 'circle',
              paddingLeft: 2,
            }}
          >
            {uniqDealNames.map(dealName => (
              <ListItem
                key={dealName}
                sx={{
                  padding: 0,
                  display: 'list-item',
                }}
              >
                <Typography variant="body2Bold">{dealName}</Typography>
              </ListItem>
            ))}
          </List>
        </>
      )}
      <Typography>{t('Do you want to make changes for all deals, or only for the current deal?')}</Typography>
    </DialogContent>
  );
};

export const usePackageConfirmationModal = () => {
  const { t } = useTranslation();
  const confirmationDialog = useConfirmationDialog();

  const showPackageConfirmation = useCallback(
    ({ relatedDealsNames, onConfirmChanges, onCancel, onUseAsTemplate }: UsePackageConfirmationModal) => {
      confirmationDialog.open({
        confirmationButtonColor: 'primary',
        confirmationLabel: t('Make changes for all deals'),
        content: <Content relatedDealsNames={relatedDealsNames} />,
        customContent: true,
        extraBtnLabel: t('Make changes for this deal only'),
        onCancel: () => {
          confirmationDialog.close();
          onCancel?.();
        },
        onConfirmed: () => {
          onConfirmChanges?.();
          confirmationDialog.close();
        },
        onExtraAction: () => {
          onUseAsTemplate?.();
          confirmationDialog.close();
        },
        showExtraBtn: true,
        title: t('Confirmation'),
      });
    },
    [confirmationDialog, t]
  );

  return { showPackageConfirmation };
};
